import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ExclamationCircleIcon,
  CalculatorIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";
import {
  checkCancellationEligibilityThunk,
  getCancellationQuoteThunk,
  processCancellationThunk,
} from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectCancellationEligibility,
  selectCancellationQuote,
  selectEligibilityLoading,
  selectQuoteLoading,
  selectProcessingLoading,
  selectEligibilityError,
  selectQuoteError,
  selectProcessingError,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";
import { clearCancellationError } from "../../../store/thunks/jewelleryPlan/cancellationThunks";

const CancellationWizard = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Selectors
  const eligibility = useSelector(selectCancellationEligibility);
  const quote = useSelector(selectCancellationQuote);
  const eligibilityLoading = useSelector(selectEligibilityLoading);
  const quoteLoading = useSelector(selectQuoteLoading);
  const processingLoading = useSelector(selectProcessingLoading);
  const eligibilityError = useSelector(selectEligibilityError);
  const quoteError = useSelector(selectQuoteError);
  const processingError = useSelector(selectProcessingError);
  const { primaryBranch } = useSelector((state) => state.branch);

  const [currentStep, setCurrentStep] = useState(1);
  const [cancellationData, setCancellationData] = useState({
    reason: "",
    reasonCategory: "CUSTOMER_REQUEST",
    refundMethod: "BANK_TRANSFER",
    refundAccount: {
      accountNumber: "",
      ifscCode: "",
      accountName: "",
      upiId: "",
    },
    skipPenalty: false,
    forceCancel: false,
    notes: "",
  });

  const reasonCategories = [
    { value: "CUSTOMER_REQUEST", label: "Customer Request" },
    { value: "FINANCIAL_HARDSHIP", label: "Financial Hardship" },
    { value: "MEDICAL_EMERGENCY", label: "Medical Emergency" },
    { value: "DEATH", label: "Death" },
    { value: "RELOCATION", label: "Relocation" },
    { value: "FRAUD_SUSPECTED", label: "Fraud Suspected" },
    { value: "ADMIN_ACTION", label: "Admin Action" },
    { value: "POLICY_VIOLATION", label: "Policy Violation" },
    { value: "OTHER", label: "Other" },
  ];

  const refundMethods = [
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "UPI", label: "UPI" },
    { value: "CASH", label: "Cash" },
    { value: "CHEQUE", label: "Cheque" },
    { value: "ADJUSTMENT", label: "Adjustment" },
  ];

  // Load eligibility on mount
  useEffect(() => {
    dispatch(checkCancellationEligibilityThunk(planId));

    return () => {
      dispatch(clearCancellationError());
    };
  }, [planId, dispatch]);

  // Handle eligibility check failure
  useEffect(() => {
    if (eligibility && !eligibility.eligibility?.canCancel) {
      const message =
        eligibility.eligibility?.reason || "Cannot cancel this plan";
      alert(message);
      navigate(`/jewellery/${planId}`);
    }
  }, [eligibility, planId, navigate]);

  const getCancellationQuote = async () => {
    try {
      await dispatch(
        getCancellationQuoteThunk({
          planId,
          reasonCategory: cancellationData.reasonCategory,
        })
      ).unwrap();
      setCurrentStep(2);
    } catch (error) {
      console.error("Failed to get quote:", error);
    }
  };

  const processCancellation = async () => {
    try {
      // Prepare account details based on refund method
      let accountDetails = {};
      if (cancellationData.refundMethod === "BANK_TRANSFER") {
        accountDetails = {
          accountNumber: cancellationData.refundAccount.accountNumber,
          ifscCode: cancellationData.refundAccount.ifscCode,
          accountName: cancellationData.refundAccount.accountName,
        };
      } else if (cancellationData.refundMethod === "UPI") {
        accountDetails = {
          upiId: cancellationData.refundAccount.upiId,
        };
      }

      const result = await dispatch(
        processCancellationThunk({
          planId,
          cancellationData: {
            reason: cancellationData.reason,
            reasonCategory: cancellationData.reasonCategory,
            refundMethod: cancellationData.refundMethod,
            refundAccount: accountDetails,
            skipPenalty: cancellationData.skipPenalty,
            forceCancel: cancellationData.forceCancel,
            notes: cancellationData.notes,
          },
        })
      ).unwrap();

      // Navigate to status page
      navigate(`/jewellery/cancellations/${planId}/status`);
    } catch (error) {
      alert("Failed to cancel plan: " + (error || "Unknown error"));
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  if (eligibilityLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (eligibilityError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-danger-light border border-danger rounded-lg p-6">
          <div className="flex items-start">
            <XCircleIcon className="h-6 w-6 text-danger mt-0.5 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-danger-dark">
                Error Loading Eligibility
              </h3>
              <p className="mt-2 text-sm text-danger-dark">
                {eligibilityError}
              </p>
              <button
                onClick={() => navigate(`/jewellery/${planId}`)}
                className="mt-4 px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark"
              >
                Back to Plan
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-background shadow rounded-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-light">
          <h2 className="text-lg font-medium text-text">Cancel Plan</h2>
          <p className="mt-1 text-sm text-neutral-dark">
            Process plan cancellation and refund
          </p>
          {eligibility?.planNumber && (
            <p className="mt-1 text-xs text-neutral-dark">
              Plan: {eligibility.planNumber}
            </p>
          )}
        </div>

        {/* Warnings */}
        {eligibility?.eligibility?.warnings?.length > 0 && (
          <div className="px-6 py-4 bg-warning-light border-b border-warning">
            <div className="flex items-start">
              <ExclamationCircleIcon className="h-5 w-5 text-warning-dark mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-warning-dark">
                  Important Warnings
                </p>
                <ul className="mt-1 text-sm text-warning-dark list-disc list-inside space-y-1">
                  {eligibility.eligibility.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Current Status Summary */}
        {eligibility?.currentStatus && (
          <div className="px-6 py-4 bg-secondary-light border-b border-secondary">
            <h3 className="text-sm font-medium text-text mb-3">
              Current Plan Status
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-neutral-dark">Months Completed</p>
                <p className="font-medium">
                  {eligibility.currentStatus.monthsCompleted}
                </p>
              </div>
              <div>
                <p className="text-neutral-dark">Total Paid</p>
                <p className="font-medium">
                  {formatAmount(eligibility.currentStatus.totalPaid)}
                </p>
              </div>
              <div>
                <p className="text-neutral-dark">Wallet Value</p>
                <p className="font-medium">
                  {formatAmount(eligibility.currentStatus.walletValue)}
                </p>
              </div>
              <div>
                <p className="text-neutral-dark">Wallet Grams</p>
                <p className="font-medium">
                  {eligibility.currentStatus.walletGrams?.toFixed(3) || 0}g
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {(quoteError || processingError) && (
          <div className="px-6 py-4 bg-danger-light border-b border-danger">
            <div className="flex items-start">
              <XCircleIcon className="h-5 w-5 text-danger mt-0.5 mr-2" />
              <div>
                <p className="text-sm font-medium text-danger-dark">Error</p>
                <p className="text-sm text-danger-dark mt-1">
                  {quoteError || processingError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="p-6">
          {currentStep === 1 ? (
            // Step 1: Cancellation Details
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-text">
                Cancellation Details
              </h3>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Reason Category *
                </label>
                <select
                  value={cancellationData.reasonCategory}
                  onChange={(e) =>
                    setCancellationData({
                      ...cancellationData,
                      reasonCategory: e.target.value,
                    })
                  }
                  className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                >
                  {reasonCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Cancellation Reason *
                </label>
                <textarea
                  value={cancellationData.reason}
                  onChange={(e) =>
                    setCancellationData({
                      ...cancellationData,
                      reason: e.target.value,
                    })
                  }
                  rows={3}
                  className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  placeholder="Provide detailed reason for cancellation..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Refund Method *
                </label>
                <select
                  value={cancellationData.refundMethod}
                  onChange={(e) =>
                    setCancellationData({
                      ...cancellationData,
                      refundMethod: e.target.value,
                    })
                  }
                  className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                >
                  {refundMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Bank Transfer Fields */}
              {cancellationData.refundMethod === "BANK_TRANSFER" && (
                <div className="space-y-4 pl-4 border-l-2 border-primary">
                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Account Number *
                    </label>
                    <input
                      type="text"
                      value={cancellationData.refundAccount.accountNumber}
                      onChange={(e) =>
                        setCancellationData({
                          ...cancellationData,
                          refundAccount: {
                            ...cancellationData.refundAccount,
                            accountNumber: e.target.value,
                          },
                        })
                      }
                      className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      IFSC Code *
                    </label>
                    <input
                      type="text"
                      value={cancellationData.refundAccount.ifscCode}
                      onChange={(e) =>
                        setCancellationData({
                          ...cancellationData,
                          refundAccount: {
                            ...cancellationData.refundAccount,
                            ifscCode: e.target.value.toUpperCase(),
                          },
                        })
                      }
                      className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      placeholder="ABCD0123456"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-1">
                      Account Name *
                    </label>
                    <input
                      type="text"
                      value={cancellationData.refundAccount.accountName}
                      onChange={(e) =>
                        setCancellationData({
                          ...cancellationData,
                          refundAccount: {
                            ...cancellationData.refundAccount,
                            accountName: e.target.value,
                          },
                        })
                      }
                      className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      required
                    />
                  </div>
                </div>
              )}

              {/* UPI Fields */}
              {cancellationData.refundMethod === "UPI" && (
                <div className="pl-4 border-l-2 border-primary">
                  <label className="block text-sm font-medium text-text mb-1">
                    UPI ID *
                  </label>
                  <input
                    type="text"
                    value={cancellationData.refundAccount.upiId}
                    onChange={(e) =>
                      setCancellationData({
                        ...cancellationData,
                        refundAccount: {
                          ...cancellationData.refundAccount,
                          upiId: e.target.value,
                        },
                      })
                    }
                    className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    placeholder="example@upi"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={cancellationData.notes}
                  onChange={(e) =>
                    setCancellationData({
                      ...cancellationData,
                      notes: e.target.value,
                    })
                  }
                  rows={2}
                  className="block w-full px-3 py-2 border border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  placeholder="Any additional information..."
                />
              </div>

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => navigate(`/jewellery/${planId}`)}
                  className="px-6 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
                >
                  Cancel
                </button>
                <button
                  onClick={getCancellationQuote}
                  disabled={
                    !cancellationData.reason ||
                    quoteLoading ||
                    (cancellationData.refundMethod === "BANK_TRANSFER" &&
                      (!cancellationData.refundAccount.accountNumber ||
                        !cancellationData.refundAccount.ifscCode ||
                        !cancellationData.refundAccount.accountName)) ||
                    (cancellationData.refundMethod === "UPI" &&
                      !cancellationData.refundAccount.upiId)
                  }
                  className="px-6 py-2 bg-primary text-text-on-primary rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {quoteLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Calculating...
                    </>
                  ) : (
                    <>
                      <CalculatorIcon className="h-4 w-4 mr-2" />
                      Get Quote
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            // Step 2: Review Quote
            quote && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-text flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-success mr-2" />
                  Cancellation Quote
                </h3>

                {/* Plan Type Info */}
                <div className="bg-secondary-light rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-dark">Plan Type</span>
                    <span className="font-medium">{quote.planType}</span>
                  </div>
                  {quote.quote?.currentRate && (
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-sm text-neutral-dark">
                        Current Rate ({quote.quote.currentRate.karat}K)
                      </span>
                      <span className="font-medium">
                        ₹{quote.quote.currentRate.ratePerGramInr}/g
                      </span>
                    </div>
                  )}
                </div>

                {/* Wallet Summary */}
                {quote.quote && (
                  <div className="bg-primary-light rounded-lg p-4">
                    <h4 className="text-sm font-medium text-text mb-3">
                      Current Wallet
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-neutral-dark">Value (INR)</p>
                        <p className="text-lg font-bold text-primary-dark">
                          {formatAmount(quote.quote.currentWalletValue)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-dark">
                          Weight (Grams)
                        </p>
                        <p className="text-lg font-bold text-primary-dark">
                          {quote.quote.currentWalletGrams?.toFixed(3) || 0}g
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Penalties */}
                {quote.quote?.penalties && (
                  <div className="bg-warning-light rounded-lg p-4">
                    <h4 className="text-sm font-medium text-text mb-3">
                      Penalties & Charges
                    </h4>
                    <div className="space-y-2">
                      {quote.quote.penalties.details?.map((detail, index) => (
                        <div
                          key={index}
                          className="flex justify-between text-sm"
                        >
                          <span className="text-neutral-dark">
                            {detail.description}
                          </span>
                          <span
                            className={`font-medium ${
                              detail.amount > 0 ? "text-danger" : "text-success"
                            }`}
                          >
                            {detail.amount > 0 ? "-" : ""}
                            {formatAmount(Math.abs(detail.amount))}
                          </span>
                        </div>
                      ))}
                      <div className="border-t pt-2 flex justify-between">
                        <span className="font-medium">Total Penalties</span>
                        <span className="font-bold text-danger">
                          -{formatAmount(quote.quote.penalties.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Benefit Reversal */}
                {quote.quote?.benefitReversal &&
                  quote.quote.benefitReversal.amount > 0 && (
                    <div className="bg-warning-light rounded-lg p-4">
                      <h4 className="text-sm font-medium text-text mb-3">
                        Benefit Reversal
                      </h4>
                      <div className="space-y-2">
                        {quote.quote.benefitReversal.details?.map(
                          (detail, index) => (
                            <div
                              key={index}
                              className="flex justify-between text-sm"
                            >
                              <span className="text-neutral-dark">
                                {detail.description}
                              </span>
                              <span className="font-medium text-warning-dark">
                                -{formatAmount(detail.amount)}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Refund Calculation */}
                {quote.quote?.refundCalculation && (
                  <div className="bg-success-light rounded-lg p-4">
                    <h4 className="text-sm font-medium text-text mb-3">
                      Final Calculation
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-dark">Gross Refund</span>
                        <span className="font-medium">
                          {formatAmount(
                            quote.quote.refundCalculation.grossRefund
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-dark">
                          Less: Penalties
                        </span>
                        <span className="font-medium text-danger">
                          -
                          {formatAmount(
                            quote.quote.refundCalculation.penalties
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-dark">
                          Less: Benefit Reversal
                        </span>
                        <span className="font-medium text-warning">
                          -
                          {formatAmount(
                            quote.quote.refundCalculation.benefitReversal
                          )}
                        </span>
                      </div>
                      <div className="border-t pt-2 flex justify-between">
                        <span className="font-bold text-lg">Net Refund</span>
                        <span className="font-bold text-lg text-success-dark">
                          {formatAmount(
                            quote.quote.refundCalculation.netRefund
                          )}
                        </span>
                      </div>
                      {quote.quote.refundCalculation.customerOwes > 0 && (
                        <div className="mt-2 p-3 bg-danger-light rounded border border-danger">
                          <div className="flex justify-between">
                            <span className="font-medium text-danger-dark">
                              Customer Owes
                            </span>
                            <span className="font-bold text-danger-dark">
                              {formatAmount(
                                quote.quote.refundCalculation.customerOwes
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quote Validity */}
                {quote.quote?.expiresAt && (
                  <div className="text-sm text-neutral-dark text-center">
                    Quote valid for {quote.quote.validFor} minutes (expires at{" "}
                    {new Date(quote.quote.expiresAt).toLocaleTimeString()})
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setCurrentStep(1)}
                    disabled={processingLoading}
                    className="px-6 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={processCancellation}
                    disabled={processingLoading}
                    className="px-6 py-2 bg-danger text-text-on-primary rounded-md text-sm font-medium hover:bg-danger-dark disabled:opacity-50 flex items-center"
                  >
                    {processingLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      "Confirm Cancellation"
                    )}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default CancellationWizard;
